import React from "react";
import { Pagination, Select } from "lib/ui";
import { Inbox } from "lucide-react";
import { useClampPage, usePagedRows } from "../../hooks/usePagedRows";
import "./PeopleDataTable.css";

const { Option } = Select;

/**
 * Custom data table with sticky actions column and built-in pagination.
 */
export function PeopleDataTable({
  columns = [],
  rows = [],
  rowKey = "_id",
  renderActions,
  emptyTitle = "No records found",
  emptyHint = "Try adjusting your search or filters.",
  pageSize: pageSizeProp = 10,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  currentPage: currentPageProp,
  onPageChange,
  wide = false,
}) {
  const [internalPage, setInternalPage] = React.useState(1);
  const [internalSize, setInternalSize] = React.useState(pageSizeProp);

  const currentPage = currentPageProp ?? internalPage;
  const setCurrentPage = onPageChange ?? setInternalPage;
  const pageSize = onPageSizeChange ? pageSizeProp : internalSize;

  const setPageSize = (size) => {
    if (onPageSizeChange) onPageSizeChange(size);
    else setInternalSize(size);
    setCurrentPage(1);
  };

  useClampPage(rows.length, currentPage, pageSize, setCurrentPage);
  const pagedRows = usePagedRows(rows, currentPage, pageSize);

  const getKey = (row, index) => {
    const k = typeof rowKey === "function" ? rowKey(row) : row?.[rowKey];
    return k ?? `row-${index}`;
  };

  const wrapClass = `pdt-wrap${wide ? " pdt-wide" : ""}`;

  if (!rows.length) {
    return (
      <div className={wrapClass}>
        <div className="pdt-empty">
          <Inbox className="mx-auto h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <p className="mt-3 text-sm font-semibold text-slate-700">{emptyTitle}</p>
          <p className="mt-1 text-xs text-slate-500">{emptyHint}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <div className="pdt-scroll">
        <table className="pdt-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                  className={col.align === "right" ? "text-right" : undefined}
                >
                  {col.header}
                </th>
              ))}
              {renderActions ? (
                <th className="pdt-actions-head">Actions</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, index) => (
              <tr key={getKey(row, index)}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={col.align === "right" ? "text-right" : undefined}
                  >
                    {col.render
                      ? col.render(row, index)
                      : row[col.dataIndex] ?? "—"}
                  </td>
                ))}
                {renderActions ? (
                  <td className="pdt-actions-cell">
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {renderActions(row, index)}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pdt-footer">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span className="text-slate-500">Rows per page</span>
          <Select
            value={pageSize}
            style={{ width: 88 }}
            onChange={setPageSize}
          >
            {pageSizeOptions.map((n) => (
              <Option key={n} value={n}>
                {n}
              </Option>
            ))}
          </Select>
        </div>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
          showTotal={(total, range) =>
            `${range[0]}–${range[1]} of ${total}`
          }
          total={rows.length}
        />
      </div>
    </div>
  );
}

export function ActionBtn({ variant = "view", onClick, children, title }) {
  const cls = {
    view: "pdt-action-btn pdt-action-btn--view",
    edit: "pdt-action-btn pdt-action-btn--edit",
    delete: "pdt-action-btn pdt-action-btn--delete",
  };
  return (
    <button
      type="button"
      title={title}
      className={cls[variant] || cls.view}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function PersonCell({ name, email, subtitle }) {
  const initial = (name || email || "?").charAt(0).toUpperCase();
  return (
    <div className="flex min-w-[200px] items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand to-brand-light text-sm font-bold text-white shadow-sm">
        {initial}
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold text-slate-900">{name || "—"}</p>
        <p className="truncate text-xs text-slate-500">{email || subtitle || "—"}</p>
      </div>
    </div>
  );
}

export function TypeBadge({ type }) {
  const styles = {
    user: "bg-violet-50 text-violet-800 ring-violet-200/80",
    delivery: "bg-sky-50 text-sky-800 ring-sky-200/80",
    driver: "bg-amber-50 text-amber-900 ring-amber-200/80",
    team: "bg-emerald-50 text-emerald-800 ring-emerald-200/80",
  };
  const labels = {
    user: "Platform user",
    delivery: "Delivery profile",
    driver: "Driver",
    team: "Team",
  };
  const key = type in styles ? type : "user";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${styles[key]}`}
    >
      {labels[key] || type}
    </span>
  );
}

export function BoolPill({ value, trueLabel = "Yes", falseLabel = "No" }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
        value
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      {value ? trueLabel : falseLabel}
    </span>
  );
}
